import { CheckCircleIcon } from "@heroicons/react/24/solid";
import React from "react";

export interface ChoiceTileOption<K extends string> {
    key: K;
    title: string;
    description?: string;
    icon?: React.ReactNode;
    disabled?: boolean;
    disabledReason?: string;
}

interface ChoiceTilesProps<K extends string> {
    options: ChoiceTileOption<K>[];
    value: K | null;
    columns: 2 | 3;
    onSelect: (key: K) => void;
}

const columnClasses: Record<2 | 3, string> = {
    2: "sm:grid-cols-2",
    3: "sm:grid-cols-3",
};

const baseTileClasses =
    "rounded-lg border p-3 text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-brand-500";
const selectedTileClasses = "border-brand-500 bg-brand-50 ring-1 ring-brand-500";
const unselectedTileClasses = "border-gray-300 bg-white hover:bg-gray-50";
const disabledTileClasses = "border-gray-200 bg-gray-50 cursor-not-allowed";

export function ChoiceTiles<K extends string>(
    props: ChoiceTilesProps<K>,
): React.ReactElement {
    const { options, value, columns, onSelect } = props;

    return (
        <div className={`grid grid-cols-1 ${columnClasses[columns]} gap-2`}>
            {options.map(option => {
                const selected = option.key === value;
                return (
                    <button
                        key={option.key}
                        type="button"
                        disabled={option.disabled}
                        aria-pressed={selected}
                        onClick={() => {
                            if (!selected && !option.disabled) {
                                onSelect(option.key);
                            }
                        }}
                        className={`${baseTileClasses} ${
                            option.disabled
                                ? disabledTileClasses
                                : selected
                                  ? selectedTileClasses
                                  : unselectedTileClasses
                        }`}>
                        <span className="flex items-start gap-2.5">
                            {option.icon ? (
                                <span
                                    className={`mt-0.5 shrink-0 ${
                                        selected ? "text-brand-600" : "text-gray-400"
                                    }`}>
                                    {option.icon}
                                </span>
                            ) : null}
                            <span className="min-w-0 flex-1">
                                <span
                                    className={`block text-sm font-medium ${
                                        option.disabled
                                            ? "text-gray-400"
                                            : "text-gray-900"
                                    }`}>
                                    {option.title}
                                </span>
                                {option.disabled && option.disabledReason ? (
                                    <span className="mt-0.5 block text-xs text-gray-400">
                                        {option.disabledReason}
                                    </span>
                                ) : option.description ? (
                                    <span
                                        className={`mt-0.5 block text-xs ${
                                            option.disabled
                                                ? "text-gray-400"
                                                : "text-gray-500"
                                        }`}>
                                        {option.description}
                                    </span>
                                ) : null}
                            </span>
                            <CheckCircleIcon
                                aria-hidden={true}
                                className={`w-5 h-5 shrink-0 text-brand-600 ${
                                    selected ? "" : "invisible"
                                }`}
                            />
                        </span>
                    </button>
                );
            })}
        </div>
    );
}
